import { MachineData } from '@/libs/pattern/patternEditor'
import { MachineSettings } from '@/generated/model/machineSettings'
import { BoxPosition } from '@/generated/model/boxPosition'
import { BoxDef, getBoxDef } from '@/libs/pattern/boxDef'
import { Borders, SafetyZone } from '@/generated/model/gripper'
import * as utils from '@/libs/pattern/utils'
import { getPalletDef, PalletDef } from '@/libs/pattern/palletDef'
import { PalletPattern } from '@/generated/model/palletPattern'
import { GripperTypeEnum } from '@/generated/model/gripperTypeEnum'
import { RobotTypeEnum } from '@/generated/model/robotTypeEnum'
import { MultipickConfigurationEnum } from '@/generated/model/multipickConfigurationEnum'
import { GripperSettings } from '@/generated/model/gripperSettings'
import { LayerPattern } from '@/generated/model/layerPattern'
import {
  checkPossibleCollision,
  getAlignedGroupsInline,
  getBoxRangesXY,
  getGripperRangesXY, getGripperRangesXYForSections, getGripperRangesXYTP2Boxes,
  getRobotMovementNumber, isFirstPlacementCollision,
  isGripperPayloadOverlap
} from '@/libs/pattern/layerSort'

export const getMaxNumberBoxes = (settings: MachineData): number[] => {
  if (isRCMultipickRobot(settings.robotType) && !isRCInlineMultipick(settings)) {
    return []
  }
  if (isInLine3(settings.robotType, settings.gripperSections)) {
    return [2, 3]
  }
  if (isQuadPick(settings) && settings.pusher) {
    return [2, 4]
  }
  if (isRI20(settings.robotType) && settings.gripperSections === 1) {
    return [1]
  }
  return [2]
}

const isRCMultipickRobot = (robotType: RobotTypeEnum): boolean => {
  return robotType === RobotTypeEnum.RC12 || robotType === RobotTypeEnum.RC20
}

export const isRCInlineMultipick = (settings: MachineData): boolean => {
  return isRCMultipickRobot(settings.robotType) &&
    settings.gripperType === GripperTypeEnum.CUSTOM_GRIPPER &&
    isInlineMultipick(settings.multipickConfiguration)
}

const isInlineMultipick = (multipickConfiguration?: MultipickConfigurationEnum): boolean => {
  return multipickConfiguration === MultipickConfigurationEnum.INLINE_CENTERED ||
    multipickConfiguration === MultipickConfigurationEnum.INLINE_EDGE_ALIGNED
}

export const isRI20 = (robotType: RobotTypeEnum): boolean => {
  return robotType === RobotTypeEnum.RI20_FANUC || robotType === RobotTypeEnum.RI20_KUKA
}

export const isQuadPick = (settings: MachineData): boolean => {
  return isRI20(settings.robotType) && settings.gripperSections === 4
}

export const isInLine3 = (robotType: RobotTypeEnum, gripperSections: number): boolean => {
  return isRI20(robotType) && gripperSections === 3
}

export const getGripperInsidePalletPercentage = (group: BoxPosition[], sections: number[], boxDef: BoxDef, palDef: PalletDef, settings: MachineData): number => {
  const gb = getGripperBorders (group[0], sections[0], boxDef, settings)
  const rotation = isQuadPick(settings) ? group[0].rotation! : 0
  const gRanges = getGripperRangesXY(gb, rotation)
  const pb = {
    top: 0,
    left: 0,
    bottom: palDef.height,
    right: palDef.width
  }
  const intersectionGP = Math.max(0, Math.min(gRanges[0].b, pb.right) - Math.max(gRanges[0].a, pb.left))
    * Math.max(0, Math.min(gRanges[1].b, pb.bottom) - Math.max(gRanges[1].a, pb.top))

  const gArea = (gRanges[0].b - gRanges[0].a) * (gRanges[1].b - gRanges[1].a)
  const pArea = palDef.width * palDef.height
  const percentage = intersectionGP / (gArea + pArea - intersectionGP)
  // console.log(`getGripperInsidePalletPercentage - intersectionGP: ${intersectionGP}, percentage: ${percentage}`)
  return percentage * 100
}

export const isGripperInsidePallet = (group: BoxPosition[], sections: number[], boxDef: BoxDef, palDef: PalletDef, settings: MachineData, side: string): boolean => {
  // console.log('[DEBUG] isGripperInsidePallet ...')
  for (let i = 0; i < group.length; i++) {
    const inside = isGripperInsidePalletSingleBox(group[i], sections[i], boxDef, palDef, settings, side)
    if (!inside) {
      return false
    }
  }
  return true
}

export const isGripperInsidePalletSingleBox = (bp: BoxPosition, gripperSection: number, boxDef: BoxDef, palDef: PalletDef, settings: MachineData, side: string): boolean => {
  const gripperBorders = getGripperBorders (bp, gripperSection, boxDef, settings)
  const boxBorders = getBoxBorders(bp, boxDef)
  const rotation = isQuadPick(settings) ? bp.rotation! : 0
  const mergedBorders = {
    left: Math.min(gripperBorders.left, boxBorders.left) * utils.projX (rotation) + Math.max(gripperBorders.left, boxBorders.left) * utils.projY (rotation),
    top: Math.min(gripperBorders.top, boxBorders.top) * utils.projX (rotation) + Math.max(gripperBorders.top, boxBorders.top) * utils.projY (rotation),
    right: Math.max(gripperBorders.right, boxBorders.right) * utils.projX (rotation) + Math.min(gripperBorders.right, boxBorders.right) * utils.projY (rotation),
    bottom: Math.max(gripperBorders.bottom, boxBorders.bottom) * utils.projX (rotation) + Math.min(gripperBorders.bottom, boxBorders.bottom) * utils.projY (rotation)
  }
  const isInside = isInsidePallet(mergedBorders, palDef, bp.rotation!, settings, side)
  // console.log('[DEBUG] isGripperInsidePalletSingleBox: ', bp.id, gripperSection, isInside)
  return isInside
}

const getBoxBorders = (bp: BoxPosition, boxDef: BoxDef) => {
  const width = boxDef.width * utils.projX(bp.rotation!) + boxDef.height * utils.projY(bp.rotation!)
  const height = boxDef.height * utils.projX(bp.rotation!) + boxDef.width * utils.projY(bp.rotation!)
  return {
    left: bp.x! - width / 2,
    top: bp.y! - height / 2,
    right: bp.x! + width / 2,
    bottom: bp.y! + height / 2
  }
}

export const isGripperInsidePalletSingleBox2 = (bp: BoxPosition, pp: PalletPattern, ms: MachineSettings, side: string, groups: number[][], gripperSections: number[][], pusherGroups: number[]): boolean => {
  const boxDef = getBoxDef (pp.box!, pp.programSettings!)
  const palDef = getPalletDef (pp.pallet!)
  const settings: MachineData = {
    gripperType: ms.gripperSettings!.gripperType!,
    gripperSections: ms.gripperSettings!.gripperSections!,
    gripperLength: ms.gripperSettings!.gripperLength!,
    gripperWidth: ms.gripperSettings!.gripperWidth!,
    pusher: ms.pusher!,
    robotType: ms.robotType!,
    boxOrientationOnConveyor: pp.programSettings!.boxOrientationOnConveyor!
  }
  const section = getBoxGripperSection(bp, groups, pusherGroups, gripperSections, settings)
  return isGripperInsidePalletSingleBox (bp, section, boxDef, palDef, settings, side)
}

export const getGripperBorders = (p: BoxPosition, gripperSection: number, boxDef: BoxDef, settings: MachineData, overlapCheck: boolean = false): Borders => {
  const b: Borders = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  }

  if (isQuadPick(settings)) {
    return getGripperBorders4Section(b, p, gripperSection, boxDef, settings, overlapCheck)
  }
  if (isInLine3(settings.robotType, settings.gripperSections)) {
    return getGripperBorders3Section(b, p, gripperSection, boxDef, settings, overlapCheck)
  }
  if (isRI20(settings.robotType) && settings.gripperSections === 2 && !overlapCheck) {
    return getGripperBorders2Section(b, p, gripperSection, boxDef, settings)
  }
  return getBoxBorders2Section(b, p, gripperSection, boxDef)
}

const getGripperBorders4Section = (b: Borders, p: BoxPosition, gripperSection: number, boxDef: BoxDef, settings: MachineData, overlapCheck: boolean = false): Borders => {
  // console.log('getGripperBorders4Section: ', JSON.stringify(p), gripperSection, JSON.stringify(boxDef), settings.boxOrientationOnConveyor)
  const rotation = p.rotation!
  const width = boxDef.width * utils.projX(rotation) + boxDef.height * utils.projY(rotation)
  const height = boxDef.height * utils.projX(rotation) + boxDef.width * utils.projY(rotation)
  const width_ = ('SHORT_SIDE_LEADING' === settings.boxOrientationOnConveyor) ? width : height
  const height_ = ('SHORT_SIDE_LEADING' === settings.boxOrientationOnConveyor) ? height : width
  let cx = 0
  let cy = 0
  let offsetX = 0
  let offsetY = 0
  const gl = (settings.gripperLength * utils.projX (rotation) + settings.gripperWidth * utils.projY (rotation))
  const gw = (settings.gripperWidth * utils.projX (rotation) + settings.gripperLength * utils.projY (rotation))
  let sectionLength = gl * 0.5
  let sectionWidth = gw * 0.5

  if (overlapCheck) {
    sectionLength = (width_ < gl / 2) ? width_ : sectionLength
    sectionWidth = (height_ < gw / 2) ? height_ : sectionWidth
  }

  const bw = width_ * 0.5
  const bh = height_ * 0.5
  if (gripperSection === 1) {
    cx = p.x! + (utils.vectorX(rotation) * bw) - (utils.vectorY(rotation) * bw)
    cy = p.y! - (utils.vectorY(rotation) * bh) - (utils.vectorX(rotation) * bh)
  } else if (gripperSection === 2) {
    cx = p.x! - (utils.vectorX(rotation) * bw) - (utils.vectorY(rotation) * bw)
    cy = p.y! + (utils.vectorY(rotation) * bh) - (utils.vectorX(rotation) * bh)
  } else if (gripperSection === 3) {
    cx = p.x! + (utils.vectorX(rotation) * bw) + (utils.vectorY(rotation) * bw)
    cy = p.y! - (utils.vectorY(rotation) * bh) + (utils.vectorX(rotation) * bh)
  } else if (gripperSection === 4) {
    cx = p.x! - (utils.vectorX(rotation) * bw) + (utils.vectorY(rotation) * bw)
    cy = p.y! + (utils.vectorY(rotation) * bh) + (utils.vectorX(rotation) * bh)
  }
  if (isSmallBox(boxDef, settings.robotType)) {
    const ov = utils.vectorX(rotation) * 111 + utils.vectorY(rotation) * 111
    offsetX = ov * utils.projX(rotation)
    offsetY = ov * utils.projY(rotation)
  }
  // TODO handle large boxes (e.g. width > gripper length/2) => RC-79

  // console.log('getGripperBorders cx, cy: ', cx, cy, p.x, p.y)
  b.left = cx - (utils.projX(rotation) * sectionLength) + (utils.projY(rotation) * sectionLength) + offsetX
  b.right = cx + (utils.projX(rotation) * sectionLength) - (utils.projY(rotation) * sectionLength) + offsetX
  b.top = cy - (utils.projX(rotation) * sectionWidth) + (utils.projY(rotation) * sectionWidth) - offsetY
  b.bottom = cy + (utils.projX(rotation) * sectionWidth) - (utils.projY(rotation) * sectionWidth) - offsetY
  // console.log('getGripperBorders4Section pos:', gripperSection, JSON.stringify(p), JSON.stringify(b))
  return b
}

const getGripperBorders3Section = (b: Borders, p: BoxPosition, gripperSection: number, boxDef: BoxDef, settings: MachineData, overlapCheck: boolean = false): Borders => {
  // console.log ('getGripperBorders3Section: ', JSON.stringify (p), gripperSection, JSON.stringify (boxDef), settings.boxOrientationOnConveyor)
  const rotation = p.rotation!
  const width = boxDef.width * utils.projX (rotation) + boxDef.height * utils.projY (rotation)
  const height = boxDef.height * utils.projX (rotation) + boxDef.width * utils.projY (rotation)
  const gl = (settings.gripperLength * utils.projX (rotation) + settings.gripperWidth * utils.projY (rotation))
  const gw = (settings.gripperWidth * utils.projX (rotation) + settings.gripperLength * utils.projY (rotation))

  let cx = 0
  let cy = 0
  const bw = width * 0.5
  const bh = height * 0.5
  let distanceX = gl * 0.5
  let distanceY = gw * 0.5

  if (overlapCheck) {
    distanceX = bw * 3 * utils.projX (rotation) + bw * utils.projY (rotation)
    distanceY = bh * utils.projX (rotation) + bh * 3 * utils.projY (rotation)
  }

  // find gripper center coordinates
  if (gripperSection === 1) {
    cx = p.x! + utils.vectorX (rotation) * width
    cy = p.y! - utils.vectorY (rotation) * height
  }
  if (gripperSection === 2) {
    // always aligned with gripper center
    cx = p.x!
    cy = p.y!
  }
  if (gripperSection === 3) {
    cx = p.x! - utils.vectorX (rotation) * width
    cy = p.y! + utils.vectorY (rotation) * height
  }
  // console.log (`getGripperBorders3Section - gripperSection [${ gripperSection }]`, { px: p.x, py: p.y, cx, cy, rotation: p.rotation })
  // gripper borders
  b.left = cx - (utils.projX (rotation) * distanceX + utils.projY (rotation) * distanceX)
  b.right = cx + (utils.projX (rotation) * distanceX + utils.projY (rotation) * distanceX)
  b.top = cy - (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  b.bottom = cy + (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  // console.log ('getGripperBorders3Section - gripperBorder: ', JSON.stringify (b))
  return b
}

/** Boxes only */
const getBoxBorders2Section = (b: Borders, p: BoxPosition, gripperSection: number, boxDef: BoxDef): Borders => {
    const rotation = p.rotation!
    const width = boxDef.width * utils.projX(rotation) + boxDef.height * utils.projY(rotation)
    const height = boxDef.height * utils.projX(rotation) + boxDef.width * utils.projY(rotation)

    // center of the gripper with two boxes
    let cx = 0
    let cy = 0

    if (gripperSection === 1) {
      cx = p.x! + utils.vectorX(rotation) * (width * 0.5)
      cy = p.y! - utils.vectorY(rotation) * (height * 0.5)
    }
    if (gripperSection === 2) {
      cx = p.x! - utils.vectorX(rotation) * (width * 0.5)
      cy = p.y! + utils.vectorY(rotation) * (height * 0.5)
    }
    // console.log(`getGripperBorders() try gripperSection [${gripperSection}]`, {px: p.x, py: p.y, cx, cy, rotation: p.rotation})

    b.left = cx - utils.projX(rotation) * width - utils.projY(rotation) * width * 0.5
    b.top = cy - (utils.projX(rotation) * (height * 0.5) + utils.projY(rotation) * height)
    b.right = cx + utils.projX(rotation) * width + utils.projY(rotation) * width * 0.5
    b.bottom = cy + (utils.projX(rotation) * (height * 0.5) + utils.projY(rotation) * height)

    return b
}

/** Gripper only */
const getGripperBorders2Section = (b: Borders, p: BoxPosition, gripperSection: number, boxDef: BoxDef, settings: MachineData): Borders => {
  // console.log ('getGripperBorders2Section: ', JSON.stringify (p), gripperSection, JSON.stringify (boxDef), settings.boxOrientationOnConveyor)
  const rotation = p.rotation!
  const width = boxDef.width * utils.projX (rotation) + boxDef.height * utils.projY (rotation)
  const height = boxDef.height * utils.projX (rotation) + boxDef.width * utils.projY (rotation)
  const gl = (settings.gripperLength * utils.projX (rotation) + settings.gripperWidth * utils.projY (rotation))
  const gw = (settings.gripperWidth * utils.projX (rotation) + settings.gripperLength * utils.projY (rotation))

  let cx = 0
  let cy = 0
  const bw = width * 0.5
  const bh = height * 0.5
  const distanceX = gl * 0.5
  const distanceY = gw * 0.5

  // find gripper center coordinates
  if (gripperSection === 1) {
    cx = p.x! + utils.vectorX (rotation) * bw
    cy = p.y! - utils.vectorY (rotation) * bh
  }
  if (gripperSection === 2) {
    cx = p.x! - utils.vectorX (rotation) * bw
    cy = p.y! + utils.vectorY (rotation) * bh
  }
  // console.log (`getGripperBorders2Section - gripperSection [${ gripperSection }]`, { px: p.x, py: p.y, cx, cy, rotation: p.rotation })
  // gripper borders
  b.left = cx - (utils.projX (rotation) * distanceX + utils.projY (rotation) * distanceX)
  b.right = cx + (utils.projX (rotation) * distanceX + utils.projY (rotation) * distanceX)
  b.top = cy - (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  b.bottom = cy + (utils.projX (rotation) * distanceY + utils.projY (rotation) * distanceY)
  // console.log ('getGripperBorders2Section - gripperBorder: ', JSON.stringify (b))
  return b
}

export const getBoxGripperSection = (p: BoxPosition, groups: number[][], pusherGroups: number[], sections: number[][], settings: MachineData): number => {
  const group = groups.filter (g => g.indexOf (p.id!) > -1)[0]
  if (group == null || group.length === 0) {
    return 1
  }
  // console.log('getBoxGripperSection - group: ', JSON.stringify(group))
  const groupIndex = groups.findIndex (g => g.includes (p.id!))

  // Check gripperSections for 2 section gripper, since group order does not reflect gripper section
  if (settings.gripperSections === 2
    || (sections[groupIndex] !== undefined && sections[groupIndex].some(s => s !== 0))) {
    const boxIndex = groups[groupIndex]!.findIndex (g => g === p.id!)
    if (boxIndex == null) {
      return 1
    }
    if (sections[groupIndex] === undefined) {
      return boxIndex + 1
    }
    const section = sections[groupIndex][boxIndex]
    return (section != null && section !== 0) ? section : (boxIndex + 1)
  }

  const pusherGrouped = pusherGroups.length > 0 && pusherGroups[groupIndex] === 1 && settings.pusher
  let index = group.indexOf(p.id!) + 1
  if (group.length === 2 && index === 2 && pusherGrouped) {
    // correction for pusher
    index += 1
  }
  // console.log ('getBoxGripperSection: ', p.id, JSON.stringify (group), index)
  return index
}

const isInsidePallet = (borders: Borders, palDef: PalletDef, rotation: number, settings: MachineData, side: string): boolean => {
  // console.log('isInsidePallet : ', JSON.stringify(borders), JSON.stringify(palDef), JSON.stringify(safetyZone))
  const safetyZone = getSafetyZone (settings.robotType, side, palDef)
  let maxBorderLeft = 0 - palDef.overhangL
  if (isRI20(settings.robotType)) {
    maxBorderLeft = 0 - safetyZone.left
  }

  let outsideLeft = borders.left < maxBorderLeft
  // console.log('isInsidePallet - outsideLeft (0|180): ', outsideLeft)
  let outsideRight = borders.right > palDef.width + Math.max(palDef.overhangR, safetyZone.right)
  let outsideTop = borders.top < 0 - Math.max(palDef.overhangT, safetyZone.top)
  let outsideBottom = borders.bottom > palDef.height + Math.max(palDef.overhangB, safetyZone.bottom)

  if (settings.gripperSections === 4 && (rotation === 90 || rotation === 270)) {
    outsideLeft = borders.left > palDef.width + Math.max(palDef.overhangR, safetyZone.right)
    outsideRight = borders.right < maxBorderLeft
    // console.log('isInsidePallet - outsideRight (90|270): ', outsideRight)
    outsideTop = borders.top > palDef.height + Math.max(palDef.overhangB, safetyZone.bottom)
    outsideBottom = borders.bottom < 0 - Math.max(palDef.overhangT, safetyZone.top)
  }
  // console.log('isInsidePallet', JSON.stringify({ outsideLeft, outsideTop, outsideRight, outsideBottom }), JSON.stringify(palDef))
  return !outsideLeft && !outsideTop && !outsideRight && !outsideBottom
}

export const getSafetyZone = (robotType: RobotTypeEnum, palletSide: string, palDef: PalletDef): SafetyZone => {
  const s: SafetyZone = {left: 0, top: 0, right: 0, bottom: 0}
  if (isRI20 (robotType)) {
    s.right = 66
    s.top = (palletSide === 'L') ? 420 : 131
    s.bottom = (palletSide === 'L') ? 131 : 420
    s.left = 1320 - palDef.width
  }
  return s
}

export const isSmallBox = (boxDef: BoxDef, robotType: RobotTypeEnum): boolean => {
  switch (robotType) {
    case 'RI20_KUKA':
      if (boxDef.actualHeight <= 140) {
        return boxDef.width <= 185
          && boxDef.height >= 105 && boxDef.height <= 200
      }
      if (boxDef.actualHeight > 140) {
        return boxDef.width <= 172.5
          && boxDef.height >= 105 && boxDef.height <= 200
      }
      return false
    case 'RI20_FANUC':
      if (boxDef.actualHeight < 150) {
        return boxDef.width >= 120 && boxDef.width < 185 &&
          boxDef.height >= 105 && boxDef.height <= 200
      }
      if (boxDef.actualHeight >= 150) {
        return boxDef.width >= 110 && boxDef.width < 160 &&
          boxDef.height >= 105 && boxDef.height <= 200
      }
      return false
    default:
      return false
  }
}

export const isRotationAllowed = (p: BoxPosition, boxDef: BoxDef, palletDef: PalletDef, side: string, settings: MachineData): boolean => {
  return true
}

export const isRotationAllowed2 = (p: BoxPosition, pp: PalletPattern, side: string, ms: MachineSettings): boolean => {
  const boxDef = getBoxDef(pp.box!, pp.programSettings!)
  const palDef = getPalletDef(pp.pallet!)
  const settings: MachineData = {
    gripperType: ms.gripperSettings!.gripperType!,
    gripperSections: ms.gripperSettings!.gripperSections!,
    gripperLength: ms.gripperSettings!.gripperLength!,
    gripperWidth: ms.gripperSettings!.gripperWidth!,
    pusher: ms.pusher!,
    robotType: ms.robotType!,
    boxOrientationOnConveyor: pp.programSettings!.boxOrientationOnConveyor!
  }
  return isRotationAllowed(p, boxDef, palDef, side, settings)
}

export const hasRotationLimitations = (robotType: RobotTypeEnum, gripperType: GripperTypeEnum ): boolean => {
  return false
}

export const detectGripperPayloadOverlap = (groups: number[][], gripperSections: number[][], positions: BoxPosition[], pusherGroups: number[], boxDef: BoxDef, settings: MachineData, side: string): Map<number, number[]> => {
  const overlapMap = new Map<number, number[]> ()
  if (!(isRI20(settings.robotType) && settings.gripperSections > 1)) {
    return overlapMap
  }
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]
    const ggs = gripperSections[i]
    // console.log ('overlapDetect - group: ', JSON.stringify (group), JSON.stringify (ggs))
    const groupBoxes: BoxPosition[] = []
    group.forEach (g => {
      const b = positions.find (pos => g === pos.id!)
      if (b !== undefined) {
        groupBoxes.push (b)
      }
    })
    const singlePlace = ggs === undefined || ggs.every(gs => gs === 0)
    groupBoxes.forEach ((bp, index) => {
      let section = (ggs !== undefined) ? ggs[index] : 0
      if (section === 0) {
        section = getBoxGripperSection(bp, groups, pusherGroups, gripperSections, settings)
        // console.log('overlapDetect - recalculate gripperSection: ', section, bp.id!)
      }
      let ranges = getBoxRangesXY (bp, boxDef)
      let fpCollisions: number[] = []

      // different range for first placement DoublePlace
      if (isQuadPick(settings) && !singlePlace
        && ((groupBoxes.length === 4 && index < 2) || (groupBoxes.length === 2 && index === 0))) {

        const gripperBorders = getGripperBorders (bp, section, boxDef, settings, true)
        ranges = (groupBoxes.length === 4)
          ? getGripperRangesXY (gripperBorders, bp.rotation)
          : getGripperRangesXYForSections(gripperBorders, bp.rotation!, ggs.slice(0, 2))
      }
      if (isInLine3(settings.robotType, settings.gripperSections) && !singlePlace
        && ((groupBoxes.length === 3 && index < 2) || (groupBoxes.length === 2 && index === 0))) {

        const fpInfo = getFirstPlacementInfo(index, bp, groupBoxes, ggs, positions, boxDef, settings, side)
        if (!fpInfo.firstPlace) {
          return
        }
        fpCollisions = fpInfo.collisions

        const gripperBorders = getGripperBorders (bp, section, boxDef, settings, true)
        ranges = (groupBoxes.length === 3)
          ? getGripperRangesXY (gripperBorders)
          : getGripperRangesXYTP2Boxes(gripperBorders, bp.rotation!)
      }
      if (settings.gripperSections === 2 && !singlePlace && index === 0) {
        const gripperBorders = getGripperBorders (bp, section, boxDef, settings, true)
        ranges = getGripperRangesXY (gripperBorders)
      }

      const movement = getRobotMovementNumber(bp.id!, positions, groups)
      let overlapIds = isGripperPayloadOverlap (groupBoxes, [], ranges, positions, boxDef)
      overlapIds = [...new Set(overlapIds.concat(fpCollisions))]

      let collisionIds: number[] = []
      if (isInLine3(settings.robotType, settings.gripperSections) || settings.gripperSections === 2) {
        const idx = positions.findIndex(p => p.id === bp.id)
        const boxesAfter = [...positions].slice(idx + 1).filter(p => !groupBoxes.map(b => b.id).includes(p.id))
        const boxesBefore = [...positions].slice(0, idx).filter(p => !groupBoxes.map(b => b.id).includes(p.id))
        const collisionIds1 = checkPossibleCollision(boxesAfter, groupBoxes, boxDef, side, false)
        const collisionIds2 = checkPossibleCollision(boxesBefore, groupBoxes, boxDef, side, true)
        // console.log (`[DEBUG] detectGripperPayloadOverlap - collisionIds: ${JSON.stringify (collisionIds)}, boxesAfter: ${JSON.stringify(boxesAfter)}, box: ${bp.id}`)
        // console.log (`[DEBUG] detectGripperPayloadOverlap - collisionIds2: ${JSON.stringify (collisionIds2)}, boxesBefore: ${JSON.stringify(boxesBefore)}, box: ${bp.id}`)
        collisionIds = [...collisionIds1, ...collisionIds2]
      }

      if (!overlapMap.has(movement[0]) && (overlapIds.length > 0 || collisionIds.length > 0)) {
        console.log (`[DEBUG] detectGripperPayloadOverlap - overlapIds: ${JSON.stringify (overlapIds)}, collisionIds: ${JSON.stringify (collisionIds)}, movement: ${movement[0]}, box: ${bp.id}`)
        overlapMap.set(movement[0], [...new Set(overlapIds.concat(collisionIds))])
      }
    })
  }
  // console.log ('overlapDetect - overlapMap: ', JSON.stringify ([...overlapMap.entries()]))
  return overlapMap
}

export const detectGripperPayloadOverlapAlt = (lp: LayerPattern, pp: PalletPattern, ms: MachineSettings, side: string) => {
  const boxDef = getBoxDef(pp.box!, pp.programSettings!)
  const settings: MachineData = {
    gripperType: ms.gripperSettings!.gripperType!,
    gripperSections: ms.gripperSettings!.gripperSections!,
    gripperLength: ms.gripperSettings!.gripperLength!,
    gripperWidth: ms.gripperSettings!.gripperWidth!,
    pusher: ms.pusher!,
    robotType: ms.robotType!,
    boxOrientationOnConveyor: pp.programSettings!.boxOrientationOnConveyor!
  }
  const groups = (side === 'L') ? lp.boxGroups! : lp.boxGroupsAlt!
  const gripperSections = (side === 'L') ? lp.gripperSections! : lp.gripperSectionsAlt!
  const positions = (side === 'L') ? lp.boxPositions! : lp.boxPositionsAlt!
  const pusherGroups = (side === 'L') ? lp.pusher! : lp.pusherAlt!
  return detectGripperPayloadOverlap(groups, gripperSections, positions, pusherGroups, boxDef, settings, side)
}

const getFirstPlacementInfo = (index: number, bp: BoxPosition, groupBoxes: BoxPosition[], gripperSections: number[], positions: BoxPosition[], boxDef: BoxDef, settings: MachineData, side: string)
  : { firstPlace: boolean, collisions: number[] } => {
  let otherSections = [gripperSections[1]]
  let splitIndex = 1
  if (groupBoxes.length === 3) {
    const isSSL = settings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING'
    const alignedGroups = getAlignedGroupsInline(groupBoxes,
      (isSSL) ? boxDef.width : boxDef.height,
      (isSSL) ? boxDef.height : boxDef.width,
      isSSL)
    if (index === 1 && alignedGroups.rest.length === 1 && alignedGroups.rest[0].id === groupBoxes[0].id) {
      // TP: skip if box 2 is not part of first placement
      return {firstPlace: false, collisions: []}
    }
    const aligned = (alignedGroups.vertical.length > 1) ? alignedGroups.vertical : ((alignedGroups.horizontal.length > 1) ? alignedGroups.horizontal : [])
    // get sections from second place group
    if (aligned.includes(bp)) {
      const result: number[] = []
      for (const a of aligned) {
        const pos = groupBoxes.indexOf(a)
        const boxSection = gripperSections[pos]
        result.push(boxSection)
      }
      otherSections = gripperSections.filter(s => !result.includes(s))
      splitIndex = aligned.length
    } else {
      const pos = groupBoxes.indexOf(alignedGroups.rest[0])
      const boxSection = gripperSections[pos]
      otherSections = gripperSections.filter(s => s !== boxSection)
    }
  }
  const firstPlaceGroup = [...groupBoxes].slice(0, splitIndex)
  const collisions = isFirstPlacementCollision(firstPlaceGroup, groupBoxes, otherSections, positions, boxDef, settings, side)
  // console.log(`[DEBUG] getFirstPlacementInfo - FP fpCollisions: ${JSON.stringify (collisions)}, otherSections: ${JSON.stringify (otherSections)}`)
  return {firstPlace: true, collisions: collisions}
}

export const getGripperTypeDescription = (gripperSettings: GripperSettings | undefined): string => {
  if (!gripperSettings || !gripperSettings.gripperType) {
    return ''
  }
  if (gripperSettings.gripperType === 'Custom Gripper') {
    const details = (gripperSettings.gripperSections != null && gripperSettings.gripperLength != null && gripperSettings.gripperWidth != null)
      ? ` (${gripperSettings.gripperSections} sections, ${gripperSettings.gripperLength} x ${gripperSettings.gripperWidth} mm)` : ''
    return `${gripperSettings.gripperType}${details}`
  }
  return gripperSettings.gripperType
}
